// ============================================================
// useVehicleFilter — 車両フィルタ状態管理（Cookie 永続化）
// ============================================================

import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Vehicle, VehicleType, StatusItem } from './types';

const COOKIE_KEY = 'calv2_vehicle_filter';

function loadCookie(): { typeIds?: string[]; statusIds?: number[]; showOutOfUse?: boolean } | null {
  try {
    const raw = document.cookie.split('; ').find(c => c.startsWith(`${COOKIE_KEY}=`));
    if (!raw) return null;
    return JSON.parse(decodeURIComponent(raw.split('=')[1]));
  } catch { return null; }
}

function saveCookie(data: { typeIds: string[]; statusIds: number[]; showOutOfUse: boolean }) {
  const val = encodeURIComponent(JSON.stringify(data));
  document.cookie = `${COOKIE_KEY}=${val}; path=/; max-age=${60 * 60 * 24 * 365}`;
}

interface UseVehicleFilterProps {
  vehicles: Vehicle[];
  vehicleTypes: VehicleType[];
  vehicleStatusList: StatusItem[];
}

interface UseVehicleFilterReturn {
  filteredVehicles: Vehicle[];
  filterOpen: boolean;
  setFilterOpen: (open: boolean) => void;
  activeTypeIds: Set<string>;
  activeStatusIds: Set<number>;
  showOutOfUse: boolean;
  toggleType: (id: string) => void;
  toggleStatus: (id: number) => void;
  toggleOutOfUse: () => void;
  selectAll: () => void;
  clearAll: () => void;
}

export function useVehicleFilter({
  vehicles,
  vehicleTypes,
  vehicleStatusList,
}: UseVehicleFilterProps): UseVehicleFilterReturn {
  const allTypeIds = useMemo(() => new Set(vehicleTypes.map(vt => String(vt.id))), [vehicleTypes]);
  const allStatusIds = useMemo(() => new Set(vehicleStatusList.map(s => s.id)), [vehicleStatusList]);

  // Cookie から初期値を復元
  const [filterOpen, setFilterOpen] = useState(false);
  const [activeTypeIds, setActiveTypeIds] = useState<Set<string>>(() => {
    const saved = loadCookie();
    return saved?.typeIds ? new Set(saved.typeIds) : new Set(allTypeIds);
  });
  const [activeStatusIds, setActiveStatusIds] = useState<Set<number>>(() => {
    const saved = loadCookie();
    return saved?.statusIds ? new Set(saved.statusIds) : new Set(allStatusIds);
  });
  const [showOutOfUse, setShowOutOfUse] = useState(() => {
    const saved = loadCookie();
    return saved?.showOutOfUse ?? false;
  });

  // マスタデータ読み込み後、Cookie にない ID を追加（新しい車種/ステータスが追加された場合）
  const initialized = useRef(false);
  useEffect(() => {
    if (initialized.current) return;
    if (allTypeIds.size === 0 || allStatusIds.size === 0) return;
    initialized.current = true;
    const saved = loadCookie();
    if (!saved) {
      setActiveTypeIds(new Set(allTypeIds));
      setActiveStatusIds(new Set(allStatusIds));
    }
  }, [allTypeIds, allStatusIds]);

  // フィルタ変更時に Cookie 保存
  useEffect(() => {
    saveCookie({
      typeIds: Array.from(activeTypeIds),
      statusIds: Array.from(activeStatusIds),
      showOutOfUse,
    });
  }, [activeTypeIds, activeStatusIds, showOutOfUse]);

  const toggleType = useCallback((id: string) => {
    setActiveTypeIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleStatus = useCallback((id: number) => {
    setActiveStatusIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleOutOfUse = useCallback(() => {
    setShowOutOfUse(prev => !prev);
  }, []);

  const selectAll = useCallback(() => {
    setActiveTypeIds(new Set(allTypeIds));
    setActiveStatusIds(new Set(allStatusIds));
    setShowOutOfUse(true);
  }, [allTypeIds, allStatusIds]);

  const clearAll = useCallback(() => {
    setActiveTypeIds(new Set());
    setActiveStatusIds(new Set());
    setShowOutOfUse(false);
  }, []);

  const filteredVehicles = useMemo(() => {
    const isAllTypes = allTypeIds.size === activeTypeIds.size;
    const isAllStatuses = allStatusIds.size === activeStatusIds.size;
    if (isAllTypes && isAllStatuses && showOutOfUse) return vehicles;

    return vehicles.filter(v => {
      if (!showOutOfUse && v.out_of_use) return false;
      if (!isAllTypes && !activeTypeIds.has(String(v.car_type_id))) return false;
      if (!isAllStatuses) {
        const matchedStatus = vehicleStatusList.find(
          s => String(s.id) === String(v.status) || s.code === v.status,
        );
        if (matchedStatus && !activeStatusIds.has(matchedStatus.id)) return false;
      }
      return true;
    });
  }, [vehicles, vehicleTypes, vehicleStatusList, activeTypeIds, activeStatusIds, showOutOfUse, allTypeIds, allStatusIds]);

  return {
    filteredVehicles,
    filterOpen,
    setFilterOpen,
    activeTypeIds,
    activeStatusIds,
    showOutOfUse,
    toggleType,
    toggleStatus,
    toggleOutOfUse,
    selectAll,
    clearAll,
  };
}
